import React, {useEffect, useState} from "react";
import {Orders} from "../../models/Orders";
import {Company} from "../../models/Company";
import {ModalShell} from "./ModalShell";
import {BASE_URLS} from "../../config/urls";
import {OrderUpsertModal} from "./OrderUpsertModal";
import "../../styles/orders-modal.css";
import {Person} from "../../models/Person";

function formatDate(dateInput: Date | string | undefined): string {
    if (!dateInput) return "—";
    const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = String(d.getFullYear()).slice(-2);
    return `${day}.${month}.${year}`;
}

export function OrderInfoModal({
                                   isOpen,
                                   onClose,
                                   order,
                                   companies,
                                   onDelete,
                                   onDownloadLabels,
                                   onOrderUpdated,
                                   person,
                               }: {
    isOpen: boolean;
    onClose: () => void;
    order: Orders | null;
    companies: Company[];
    onDelete: (order: Orders) => void;
    onDownloadLabels: (order: Orders) => void;
    onOrderUpdated: (order: Orders) => void;
    person: Person;
}) {
    const [isEditOpen, setEditOpen] = useState(false);

    useEffect(() => {
        if (!isOpen) setEditOpen(false);
    }, [isOpen]);

    if (!isOpen || !order) return null;

    const pay = () => window.location.assign(`${BASE_URLS.app}/payment/${order.id}`);

    return (
        <>
            <ModalShell title="Информация о заказе" subtitle="Данные поставки и действия" onClose={onClose}>
                <div className="order-modal-grid">
                    <div className="order-modal-row">
                        <div className="order-modal-label">Дата заказа</div>
                        <div className="order-modal-value">{formatDate((order as any).orderDate)}</div>
                    </div>

                    <div className="order-modal-row">
                        <div className="order-modal-label">Юридическое лицо</div>
                        <div className="order-modal-value">
                            {order.entity} {(order as any).inn ? `(${(order as any).inn})` : ""}
                        </div>
                    </div>

                    <div className="order-modal-row">
                        <div className="order-modal-label">Номер телефона</div>
                        <div className="order-modal-value">{(order as any).phoneNumber ?? "—"}</div>
                    </div>

                    <div className="order-modal-row">
                        <div className="order-modal-label">Склад назначения</div>
                        <div className="order-modal-value">
                            {order.sendCity} ({order.store})
                        </div>
                    </div>

                    {order.store === "Ozon" && (
                        <div className="order-modal-row">
                            <div className="order-modal-label">Номер поставки (Ozon)</div>
                            <div className="order-modal-value">{(order as any).numberOzon ?? "—"}</div>
                        </div>
                    )}

                    <div className="order-modal-row">
                        <div className="order-modal-label">Дата поставки</div>
                        <div className="order-modal-value">{formatDate(order.departureDate)}</div>
                    </div>

                    <div className="order-modal-row">
                        <div className="order-modal-label">Тип отправки</div>
                        <div className="order-modal-value">{(order as any).supplyType ?? "—"}</div>
                    </div>

                    <div className="order-modal-row order-modal-row--boxes">
                        <div className="order-modal-label">Коробки</div>
                        <div className="order-modal-value">
                            {order.boxes?.length ? (
                                <div className="order-modal-boxes">
                                    {order.boxes.map((box: any, idx: number) => (
                                        <div key={idx} className="order-modal-box">
                                            {box.length}/{box.height}/{box.width} — {box.amount}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                "—"
                            )}
                        </div>
                    </div>

                    <div className="order-modal-row">
                        <div className="order-modal-label">Объём</div>
                        <div className="order-modal-value">{order.volume ?? "—"}</div>
                    </div>

                    <div className="order-modal-row">
                        <div className="order-modal-label">Цена</div>
                        <div className="order-modal-value">{order.price ?? "—"}</div>
                    </div>

                    <div className="order-modal-row">
                        <div className="order-modal-label">Забор со склада</div>
                        <div className="order-modal-value">{order.willTaken ? "Да" : "Нет"}</div>
                    </div>

                    <div className="order-modal-row">
                        <div className="order-modal-label">Оплата на сайте</div>
                        <div className="order-modal-value">{order.paymentSite ? "Да" : "Нет"}</div>
                    </div>

                    <div className="order-modal-row">
                        <div className="order-modal-label">Комментарии</div>
                        <div className="order-modal-value">{(order as any).comment ?? "—"}</div>
                    </div>

                    <div className="order-modal-row">
                        <div className="order-modal-label">Статус</div>
                        <div className="order-modal-value">{order.status ?? "—"}</div>
                    </div>

                    <div className="order-modal-row">
                        <div className="order-modal-label">Статус оплаты</div>
                        <div className="order-modal-value">{order.paymentStatus ? "Оплачен" : "Не оплачен"}</div>
                    </div>
                </div>

                <div className="order-modal-actions">
                    {order.paymentSite && !order.paymentStatus && (
                        <button className="btn-primary" onClick={pay}>
                            Оплатить
                        </button>
                    )}

                    {order.changeable && (
                        <button className="btn-secondary" onClick={() => setEditOpen(true)}>
                            Изменить
                        </button>
                    )}

                    <button
                        className="btn-secondary"
                        onClick={() => onDownloadLabels(order)}
                        disabled={!order.boxes?.length}
                        title={!order.boxes?.length ? "Нет коробок для этикеток" : undefined}
                    >
                        Скачать этикетки
                    </button>

                    {order.changeable && (
                        <button className="btn-danger" onClick={() => onDelete(order)}>
                            Удалить
                        </button>
                    )}

                    <button className="btn-secondary" onClick={onClose}>
                        Закрыть
                    </button>
                </div>
            </ModalShell>

            {/* ✅ Вторая модалка — редактирование */}
            {order.supply &&
              <OrderUpsertModal
                isOpen={isEditOpen}
                onClose={() => setEditOpen(false)}
                order={order}
                supply={order.supply}              // ✅ важно для складов
                companies={companies}
                onSaved={(updated) => {
                    onOrderUpdated(updated);
                    setEditOpen(false);
                }}
                person={person}
              />}
        </>
    );
}
